import type { FieldSensitivity } from '../types/FieldManifest';

/**
 * Canonical ordering for field sensitivity across editor, viewer, and
 * server-side processing paths. Higher indexes require stronger access.
 */
export const FIELD_SENSITIVITY_ORDER: readonly FieldSensitivity[] = [
  'public',
  'internal',
  'manager',
  'hr_confidential',
  'hr_restricted',
  'sensitive_pii',
];

export const PROTECTED_YJS_MINIMUM_SENSITIVITY: FieldSensitivity = 'manager';

export function fieldSensitivityRank(level: FieldSensitivity): number {
  const index = FIELD_SENSITIVITY_ORDER.indexOf(level);
  return index >= 0 ? index : 0;
}

export function isSensitivityAtLeast(
  level: FieldSensitivity,
  minimum: FieldSensitivity,
): boolean {
  return fieldSensitivityRank(level) >= fieldSensitivityRank(minimum);
}

export function isProtectedYjsSensitivity(level: FieldSensitivity): boolean {
  return isSensitivityAtLeast(level, PROTECTED_YJS_MINIMUM_SENSITIVITY);
}

/**
 * Defensive aliases for imported datasets whose columns predate field packs.
 * These keys are also used by viewer redaction so inspection and publication
 * cannot silently disagree about well-known confidential data.
 */
export const KNOWN_PROTECTED_FIELD_KEYS: ReadonlySet<string> = new Set([
  'ssn',
  'sin',
  'nationalId',
  'nationalInsuranceNumber',
  'taxId',
  'passportNumber',
  'dateOfBirth',
  'dob',
  'birthDate',
  'salary',
  'baseSalary',
  'bonus',
  'bonusTarget',
  'totalComp',
  'annualCost',
  'compaRatio',
  'payRangeMin',
  'payRangeMid',
  'payRangeMax',
  'gender',
  'ethnicity',
  'nationality',
  'disabilityStatus',
  'veteranStatus',
  'maritalStatus',
  'visaStatus',
  'visaExpiry',
  'internalNote',
  'internalNotes',
  'successionPayload',
  'orgPlanningPayload',
]);
